/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Line Item Pricing Calculator
 * Calculates unit price, total price, and profit percentage using budget rates.
 * Delegates to pricing-helpers for formula internals and labor-hour-absorption for that method.
 */

import { formatMoneyDiagnosticDollars } from "@/lib/utils/money/cents";
import { BUSINESS_RULES } from "@/lib/constants/business/rules";
import type { BudgetRates } from "./estimates-types";
import {
  applyOverheadConstraints,
  resolveContingencyBase,
  resolveOverheadBasis,
  shouldApplyContingency,
  shouldUseRevenueFormula,
} from "./estimates-calculations-helpers";
import {
  calculateInitialOverhead,
  calculatePricingFromBudgetRates,
  calculateWithProfitOverride,
  calculateZeroProfitResult,
  determineMarginRate,
  determineProfitMargin,
} from "./pricing-helpers";
import { calculateLaborHourAbsorptionPricing } from "./labor-hour-absorption";

// ============================================================================
// TYPES
// ============================================================================

export interface LineItemPricingResult {
  unitPrice: number;
  totalPrice: number;
  marginPercentage: number;
  profitPercentage: number;
  /** Warnings about pricing issues (e.g., negative profit from misconfigured rates) */
  warnings?: string[];
}

// ============================================================================
// MAIN FUNCTION
// ============================================================================

/**
 * Calculate line item pricing (unit price, total price, profit %) using budget rates.
 * Supports both single and activity-based overhead recovery methods.
 * Determines category-specific margin and overhead based on material_cost, labor_cost, equipment_cost.
 *
 * Calculation Flow:
 * 1. Base Cost (original cost)
 * 2. + Tax on Cost (if taxRateCost provided and not tax exempt) - applied BEFORE overhead
 * 3. = Adjusted Cost
 * 4. + Overhead (calculated on adjusted cost)
 * 5. + Contingency (if configured in budget rates, applied after overhead)
 * 6. = Base for Margin
 * 7. Apply margin: Price = Base / (1 - margin)
 *
 * @param taxRateCost - Optional tax rate on cost (as percentage, e.g., 8.5 for 8.5%)
 * @param isTaxExempt - Optional flag to exclude tax on cost
 */
export function calculateLineItemPricing(
  unitCost: number,
  totalCost: number,
  materialCost: number = 0,
  laborCost: number = 0,
  equipmentCost: number = 0,
  budgetRates?: BudgetRates | null,
  profitPercentageOverride?: number,
  taxRateCost?: number,
  isTaxExempt?: boolean,
  estimateSubtotalForOverheadAllocation?: number,
  subcontractorCost: number = 0,
): LineItemPricingResult {
  // Calculate tax on cost if applicable
  const taxOnCost =
    !isTaxExempt && taxRateCost !== undefined && taxRateCost > 0
      ? totalCost * (taxRateCost / 100)
      : 0;

  const adjustedTotalCost = totalCost + taxOnCost;

  // Labor hour absorption: overhead loaded onto labor hours only,
  // materials/subs/equipment get flat markup instead
  if (budgetRates?.overheadRecoveryMethod === "labor_hour_absorption") {
    return calculateLaborHourAbsorptionPricing(
      unitCost,
      totalCost,
      adjustedTotalCost,
      laborCost,
      materialCost,
      equipmentCost,
      subcontractorCost,
      budgetRates,
      profitPercentageOverride,
    );
  }

  // Calculate overhead
  const overheadRecoveryMethod = budgetRates?.overheadRecoveryMethod;
  const overheadBasis = resolveOverheadBasis(budgetRates);
  const overheadRate =
    budgetRates?.overheadRate ?? BUSINESS_RULES.DEFAULT_OVERHEAD_RATE;

  let overhead = calculateInitialOverhead(
    adjustedTotalCost,
    totalCost,
    materialCost,
    laborCost,
    equipmentCost,
    overheadRate,
    overheadRecoveryMethod,
    overheadBasis,
    budgetRates,
    estimateSubtotalForOverheadAllocation,
    subcontractorCost,
  );
  overhead = applyOverheadConstraints(overhead, budgetRates);

  // Determine margin rate (fallback) and profit margin (preferred)
  const marginRate = determineMarginRate(budgetRates);
  const resolvedProfitMargin = determineProfitMargin(budgetRates);

  // Calculate contingency
  const contingencyRate = shouldApplyContingency(budgetRates)
    ? budgetRates?.contingencyRate
    : undefined;
  const contingencyBase = resolveContingencyBase(
    adjustedTotalCost,
    overhead,
    budgetRates,
  );
  const contingency =
    contingencyRate !== undefined && contingencyRate > 0
      ? contingencyBase * contingencyRate
      : 0;

  const baseForMargin = adjustedTotalCost + overhead + contingency;

  // Check if revenue formula should be used
  const useRevenueFormula = shouldUseRevenueFormula(budgetRates);

  // Effective contingency rate for revenue formula
  const effectiveContingencyRate =
    useRevenueFormula && shouldApplyContingency(budgetRates)
      ? (budgetRates?.contingencyRate ?? 0)
      : 0;

  // Handle zero profit override
  if (
    profitPercentageOverride !== undefined &&
    profitPercentageOverride === 0
  ) {
    if (useRevenueFormula) {
      const denominator = 1 - overheadRate - effectiveContingencyRate;
      const totalPrice =
        denominator > 0 ? adjustedTotalCost / denominator : baseForMargin;
      const quantity = unitCost > 0 ? totalCost / unitCost : 1;
      const unitPrice = quantity > 0 ? totalPrice / quantity : unitCost;
      return {
        unitPrice,
        totalPrice,
        marginPercentage: 0,
        profitPercentage: 0,
      };
    }
    return calculateZeroProfitResult(unitCost, totalCost, baseForMargin);
  }

  // Calculate pricing using margin formula
  const result = calculatePricingFromBudgetRates(
    adjustedTotalCost,
    totalCost,
    overhead,
    contingency,
    baseForMargin,
    overheadRate,
    marginRate,
    useRevenueFormula,
    resolvedProfitMargin,
    effectiveContingencyRate,
    budgetRates,
  );

  // Apply profit override if provided
  if (
    profitPercentageOverride !== undefined &&
    profitPercentageOverride !== 0
  ) {
    return calculateWithProfitOverride(
      adjustedTotalCost,
      totalCost,
      unitCost,
      overhead,
      contingency,
      overheadRate,
      overheadRecoveryMethod,
      useRevenueFormula,
      profitPercentageOverride,
      effectiveContingencyRate,
      budgetRates,
    );
  }

  // Calculate unit price and margin percentage
  const quantity = unitCost > 0 ? totalCost / unitCost : 1;
  const marginPercentage = (resolvedProfitMargin ?? marginRate) * 100;

  let unitPrice: number;
  let profitPercentage = result.profitPercentage;
  if (quantity > 0) {
    unitPrice = result.totalPrice / quantity;
  } else if (unitCost > 0) {
    const effectiveMargin = resolvedProfitMargin ?? marginRate;
    if (useRevenueFormula) {
      const denominator =
        1 - effectiveMargin - overheadRate - effectiveContingencyRate;
      unitPrice = denominator > 0 ? unitCost / denominator : unitCost;
    } else {
      const unitOverhead = unitCost * overheadRate;
      const unitContingencyBase = unitCost + unitOverhead;
      const unitContingency =
        contingencyRate !== undefined && contingencyRate > 0
          ? unitContingencyBase * contingencyRate
          : 0;
      const unitBase = unitCost + unitOverhead + unitContingency;
      unitPrice =
        effectiveMargin < 1 ? unitBase / (1 - effectiveMargin) : unitBase;
    }
    profitPercentage = marginPercentage;
  } else {
    unitPrice = unitCost;
  }

  // Safety rail: never return a line item priced below cost. If the formula
  // produced one (negative profit from misconfigured rates, bad override, or
  // unit/cost mismatch like the 2026-04-21 "Starter Fertilizer 5lb bag at
  // $55 cost, $10.36 price" incident), clamp to cost with a loud warning so
  // downstream writers can see and surface it. Prefer a visible zero-margin
  // row over a silent loss.
  const warnings: string[] = [];
  if (result.warning) warnings.push(result.warning);
  let safeTotalPrice = result.totalPrice;
  let safeUnitPrice = unitPrice;
  let safeProfitPercentage = profitPercentage;
  if (safeTotalPrice < adjustedTotalCost && adjustedTotalCost > 0) {
    warnings.push(
      `Computed total_price (${formatMoneyDiagnosticDollars(safeTotalPrice)}) is below cost (${formatMoneyDiagnosticDollars(adjustedTotalCost)}). Clamping to cost (0% margin). Check overhead / profit margin / tax configuration.`,
    );
    safeTotalPrice = adjustedTotalCost;
    safeUnitPrice =
      unitCost > 0 ? adjustedTotalCost / (totalCost / unitCost) : unitCost;
    safeProfitPercentage = 0;
  }

  return {
    unitPrice: safeUnitPrice,
    totalPrice: safeTotalPrice,
    marginPercentage,
    profitPercentage: safeProfitPercentage,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}
